/**
 * Redux-to-Zustand Sync Layer
 *
 * This sync layer bridges Redux and Zustand during the migration period.
 * It syncs Redux state into Zustand stores, allowing components to gradually
 * migrate to Zustand while maintaining compatibility with existing Redux code.
 *
 * Once all domains are migrated to Zustand:
 *   - Delete this entire useReduxSync hook
 *   - Remove Redux Provider from the app
 *   - Uninstall redux, react-redux, and @reduxjs/toolkit
 */

import { useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import type { City } from "@/apis/city/city-apis";
import type { CityWhitelabelMapping } from "@/types/reducer-states";
import type { RootState } from "@/types/redux";
import { useAuthStore } from "./authStore";
import { useCitiesStore } from "./citiesStore";
import { useCityStore } from "./cityStore";
import { useRentalsStore } from "./rentalsStore";
import { useRewardsStore } from "./rewardsStore";
import { useStationsStore } from "./stationsStore";
import { useUsersStore } from "./usersStore";
import { useVehicleStore } from "./vehicleStore";

const EMPTY_ARRAY: never[] = [];

const asArray = <T>(value: T[] | null | undefined): T[] =>
  Array.isArray(value) ? value : EMPTY_ARRAY;

/**
 * Hook to sync Redux state to Zustand stores.
 * This enables incremental migration from Redux to Zustand.
 *
 * Usage: Call this hook once at the app root (e.g., DashboardScreen)
 * to keep Zustand stores in sync with Redux state.
 *
 * Once migration is complete, Redux can be removed and this hook deleted.
 */
export function useReduxSync() {
  const reduxCitiesList = useSelector(
    (state: RootState) => state.cities.citiesList,
    shallowEqual
  ) as City[];
  const reduxSavedCity = useSelector(
    (state: RootState) => state.cities.savedCity
  );
  const reduxSavedCityFullInfo = useSelector(
    (state: RootState) => state.cities.savedCityFullInfo,
    shallowEqual
  ) as City | null;
  const reduxSavedWhitelabel = useSelector(
    (state: RootState) => state.cities.savedWhitelabel
  );
  const reduxSavedWhitelabelFullInfo = useSelector(
    (state: RootState) => state.cities.savedWhitelabelFullInfo,
    shallowEqual
  );
  const reduxWhitelabels = useSelector(
    (state: RootState) => state.cities.whiteLabels,
    shallowEqual
  );
  const reduxWhiteLabelList = useSelector(
    (state: RootState) => state.cities.whiteLabelList,
    shallowEqual
  );
  const reduxCityWhitelabelMapping = useSelector(
    (state: RootState) => state.cities.cityWhitelabelMapping,
    shallowEqual
  ) as CityWhitelabelMapping[] | null;

  const reduxAdminDetails = useSelector(
    (state: RootState) => state.auth.adminDetails,
    shallowEqual
  );
  const reduxWhiteLabelCode = useSelector(
    (state: RootState) => state.auth.whiteLabelCode
  );
  const reduxErrorMessage = useSelector(
    (state: RootState) => state.auth.errorMessage
  );

  const reduxVehicleStatuses = useSelector(
    (state: RootState) => state.vehicle.statuses,
    shallowEqual
  );
  const reduxLockStatuses = useSelector(
    (state: RootState) => state.vehicle.lockStatuses,
    shallowEqual
  );
  const reduxBrands = useSelector(
    (state: RootState) => state.vehicle.brands,
    shallowEqual
  );
  const reduxBikeTypes = useSelector(
    (state: RootState) => state.vehicle.bikeTypes,
    shallowEqual
  );
  const reduxBikeStatusByIdSuccess = useSelector(
    (state: RootState) => state.vehicle.bikeStatusByIdSuccess
  );
  const reduxBrokenBikeStatuses = useSelector(
    (state: RootState) => state.vehicle.brokenBikeStatuses,
    shallowEqual
  );

  const reduxRentalBike = useSelector(
    (state: RootState) => state.rentals.rentalBike,
    shallowEqual
  );

  const reduxInternalUser = useSelector(
    (state: RootState) => state.users.internalUser,
    shallowEqual
  );
  const reduxInternalUserSuccess = useSelector(
    (state: RootState) => state.users.internalUserSuccess
  );

  const reduxRewardPrograms = useSelector(
    (state: RootState) => state.rewards?.rewardPrograms,
    shallowEqual
  );
  const reduxRewardProgramsSuccess = useSelector(
    (state: RootState) => state.rewards?.rewardProgramsSuccess
  );
  const reduxCreateRewardProgramSuccess = useSelector(
    (state: RootState) => state.rewards?.createRewardProgramSuccess
  );
  const reduxUpdateRewardProgramSuccess = useSelector(
    (state: RootState) => state.rewards?.updateRewardProgramSuccess
  );
  const reduxRewardRules = useSelector(
    (state: RootState) => state.rewards?.rewardRules,
    shallowEqual
  );
  const reduxRewardRulesSuccess = useSelector(
    (state: RootState) => state.rewards?.rewardRulesSuccess
  );
  const reduxCreateRewardRuleSuccess = useSelector(
    (state: RootState) => state.rewards?.createRewardRuleSuccess
  );
  const reduxUpdateRewardRuleSuccess = useSelector(
    (state: RootState) => state.rewards?.updateRewardRuleSuccess
  );
  const reduxRewardClaims = useSelector(
    (state: RootState) => state.rewards?.rewardClaims,
    shallowEqual
  );
  const reduxRewardClaimsSuccess = useSelector(
    (state: RootState) => state.rewards?.rewardClaimsSuccess
  );

  const reduxBlockConfigs = useSelector(
    (state: RootState) => state.stations?.blockConfigs,
    shallowEqual
  );
  const reduxBlockConfigsSuccess = useSelector(
    (state: RootState) => state.stations?.blockConfigsSuccess
  );
  const reduxSaveBlockConfigSuccess = useSelector(
    (state: RootState) => state.stations?.saveBlockConfigSuccess
  );
  const reduxStationsByCity = useSelector(
    (state: RootState) => state.stations?.stationsByCity,
    shallowEqual
  );
  const reduxLinkedStations = useSelector(
    (state: RootState) => state.stations?.linkedStations,
    shallowEqual
  );

  const { setCitiesList, selectCity, selectWhitelabel, setWhitelabels } =
    useCityStore();
  const {
    setCitiesList: setCitiesStoreCitiesList,
    setSavedCity: setCitiesStoreSavedCity,
    setSavedWhitelabel: setCitiesStoreSavedWhitelabel,
    setWhiteLabels: setCitiesStoreWhiteLabels,
    setWhiteLabelList: setCitiesStoreWhiteLabelList,
    setCityWhitelabelMapping: setCitiesStoreCityWhitelabelMapping,
  } = useCitiesStore();
  const { setAdminDetails, setWhiteLabelCode, setErrorMessage } =
    useAuthStore();
  const {
    setStatuses: setVehicleStatuses,
    setLockStatuses: setVehicleLockStatuses,
    setBrands: setVehicleBrands,
    setBikeTypes: setVehicleBikeTypes,
    setBikeStatusByIdSuccess: setVehicleBikeStatusByIdSuccess,
    setBrokenBikeStatuses: setVehicleBrokenBikeStatuses,
  } = useVehicleStore();
  const { setRentalBike } = useRentalsStore();
  const { setInternalUser, setInternalUserSuccess } = useUsersStore();
  const {
    setRewardPrograms,
    setRewardProgramsSuccess,
    setCreateRewardProgramSuccess,
    setUpdateRewardProgramSuccess,
    setRewardRules,
    setRewardRulesSuccess,
    setCreateRewardRuleSuccess,
    setUpdateRewardRuleSuccess,
    setRewardClaims,
    setRewardClaimsSuccess,
  } = useRewardsStore();
  const {
    setBlockConfigs,
    setBlockConfigsSuccess,
    setSaveBlockConfigSuccess,
    setStationsByCity,
    setLinkedStations,
  } = useStationsStore();

  const citiesList = asArray(reduxCitiesList);
  const whitelabels = asArray(reduxWhitelabels);
  const whiteLabelList = asArray(reduxWhiteLabelList);
  const cityWhitelabelMapping = Array.isArray(reduxCityWhitelabelMapping)
    ? reduxCityWhitelabelMapping
    : null;

  useEffect(() => {
    if (citiesList.length > 0) {
      setCitiesList(citiesList);
    }
  }, [citiesList, setCitiesList]);

  useEffect(() => {
    if (citiesList.length > 0) {
      setCitiesStoreCitiesList(citiesList);
    }
  }, [citiesList, setCitiesStoreCitiesList]);

  useEffect(() => {
    if (whitelabels.length > 0) {
      setWhitelabels(whitelabels);
    }
  }, [whitelabels, setWhitelabels]);

  useEffect(() => {
    if (whitelabels.length > 0) {
      setCitiesStoreWhiteLabels(whitelabels);
    }
  }, [whitelabels, setCitiesStoreWhiteLabels]);

  useEffect(() => {
    if (whiteLabelList.length > 0) {
      setCitiesStoreWhiteLabelList(whiteLabelList);
    }
  }, [whiteLabelList, setCitiesStoreWhiteLabelList]);

  useEffect(() => {
    setCitiesStoreCityWhitelabelMapping(cityWhitelabelMapping);
  }, [cityWhitelabelMapping, setCitiesStoreCityWhitelabelMapping]);

  useEffect(() => {
    selectCity(reduxSavedCity, reduxSavedCityFullInfo);
  }, [reduxSavedCity, reduxSavedCityFullInfo, selectCity]);

  useEffect(() => {
    setCitiesStoreSavedCity(reduxSavedCity, reduxSavedCityFullInfo);
  }, [reduxSavedCity, reduxSavedCityFullInfo, setCitiesStoreSavedCity]);

  useEffect(() => {
    selectWhitelabel(reduxSavedWhitelabel, reduxSavedWhitelabelFullInfo);
  }, [reduxSavedWhitelabel, reduxSavedWhitelabelFullInfo, selectWhitelabel]);

  useEffect(() => {
    setCitiesStoreSavedWhitelabel(
      reduxSavedWhitelabel,
      reduxSavedWhitelabelFullInfo
    );
  }, [
    reduxSavedWhitelabel,
    reduxSavedWhitelabelFullInfo,
    setCitiesStoreSavedWhitelabel,
  ]);

  useEffect(() => {
    setAdminDetails(reduxAdminDetails);
  }, [reduxAdminDetails, setAdminDetails]);

  useEffect(() => {
    setWhiteLabelCode(reduxWhiteLabelCode);
  }, [reduxWhiteLabelCode, setWhiteLabelCode]);

  useEffect(() => {
    setErrorMessage(reduxErrorMessage);
  }, [reduxErrorMessage, setErrorMessage]);

  useEffect(() => {
    if (reduxVehicleStatuses?.length > 0) {
      setVehicleStatuses(reduxVehicleStatuses);
    }
  }, [reduxVehicleStatuses, setVehicleStatuses]);

  useEffect(() => {
    if (reduxLockStatuses?.length > 0) {
      setVehicleLockStatuses(reduxLockStatuses);
    }
  }, [reduxLockStatuses, setVehicleLockStatuses]);

  useEffect(() => {
    if (reduxBrands?.length > 0) {
      setVehicleBrands(reduxBrands);
    }
  }, [reduxBrands, setVehicleBrands]);

  useEffect(() => {
    if (reduxBikeTypes?.length > 0) {
      setVehicleBikeTypes(reduxBikeTypes);
    }
  }, [reduxBikeTypes, setVehicleBikeTypes]);

  useEffect(() => {
    setVehicleBikeStatusByIdSuccess(reduxBikeStatusByIdSuccess);
  }, [reduxBikeStatusByIdSuccess, setVehicleBikeStatusByIdSuccess]);

  useEffect(() => {
    if (reduxBrokenBikeStatuses) {
      setVehicleBrokenBikeStatuses(reduxBrokenBikeStatuses);
    }
  }, [reduxBrokenBikeStatuses, setVehicleBrokenBikeStatuses]);

  useEffect(() => {
    setRentalBike(reduxRentalBike);
  }, [reduxRentalBike, setRentalBike]);

  useEffect(() => {
    setInternalUser(reduxInternalUser);
  }, [reduxInternalUser, setInternalUser]);

  useEffect(() => {
    setInternalUserSuccess(reduxInternalUserSuccess);
  }, [reduxInternalUserSuccess, setInternalUserSuccess]);

  useEffect(() => {
    setRewardPrograms(reduxRewardPrograms);
  }, [reduxRewardPrograms, setRewardPrograms]);

  useEffect(() => {
    setRewardProgramsSuccess(reduxRewardProgramsSuccess);
  }, [reduxRewardProgramsSuccess, setRewardProgramsSuccess]);

  useEffect(() => {
    setCreateRewardProgramSuccess(reduxCreateRewardProgramSuccess);
  }, [reduxCreateRewardProgramSuccess, setCreateRewardProgramSuccess]);

  useEffect(() => {
    setUpdateRewardProgramSuccess(reduxUpdateRewardProgramSuccess);
  }, [reduxUpdateRewardProgramSuccess, setUpdateRewardProgramSuccess]);

  useEffect(() => {
    setRewardRules(reduxRewardRules);
  }, [reduxRewardRules, setRewardRules]);

  useEffect(() => {
    setRewardRulesSuccess(reduxRewardRulesSuccess);
  }, [reduxRewardRulesSuccess, setRewardRulesSuccess]);

  useEffect(() => {
    setCreateRewardRuleSuccess(reduxCreateRewardRuleSuccess);
  }, [reduxCreateRewardRuleSuccess, setCreateRewardRuleSuccess]);

  useEffect(() => {
    setUpdateRewardRuleSuccess(reduxUpdateRewardRuleSuccess);
  }, [reduxUpdateRewardRuleSuccess, setUpdateRewardRuleSuccess]);

  useEffect(() => {
    setRewardClaims(reduxRewardClaims);
  }, [reduxRewardClaims, setRewardClaims]);

  useEffect(() => {
    setRewardClaimsSuccess(reduxRewardClaimsSuccess);
  }, [reduxRewardClaimsSuccess, setRewardClaimsSuccess]);

  useEffect(() => {
    setBlockConfigs(reduxBlockConfigs);
  }, [reduxBlockConfigs, setBlockConfigs]);

  useEffect(() => {
    setBlockConfigsSuccess(reduxBlockConfigsSuccess);
  }, [reduxBlockConfigsSuccess, setBlockConfigsSuccess]);

  useEffect(() => {
    setSaveBlockConfigSuccess(reduxSaveBlockConfigSuccess);
  }, [reduxSaveBlockConfigSuccess, setSaveBlockConfigSuccess]);

  useEffect(() => {
    if (reduxStationsByCity?.length > 0) {
      setStationsByCity(reduxStationsByCity);
    }
  }, [reduxStationsByCity, setStationsByCity]);

  useEffect(() => {
    if (reduxLinkedStations?.length > 0) {
      setLinkedStations(reduxLinkedStations);
    }
  }, [reduxLinkedStations, setLinkedStations]);
}
